import { z } from 'zod';

export const TASK_STATUSES = ['da_fare', 'in_corso', 'fatta'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_PRIORITIES = ['bassa', 'normale', 'alta'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_SOURCES = ['manuale', 'ai', 'sistema'] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

export const recurrenceSchema = z.object({
  freq: z.enum(['daily', 'weekly', 'monthly']),
  interval: z.number().int().min(1).max(12).default(1),
  /** Giorni della settimana per le ripetizioni settimanali: 1 = lunedì … 7 = domenica. */
  weekdays: z.array(z.number().int().min(1).max(7)).max(7).optional(),
});
export type Recurrence = z.infer<typeof recurrenceSchema>;

export const checklistItemSchema = z.object({
  id: z.string().min(1).max(40),
  text: z.string().trim().min(1).max(300),
  done: z.boolean(),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

const isoDateTime = z.iso.datetime({ offset: true });

export const taskCreateSchema = z.object({
  title: z.string().trim().min(1, 'Scrivi cosa c’è da fare').max(300),
  pageId: z.string().nullable().optional(),
  notes: z.string().trim().max(10000).optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  dueAt: isoDateTime.nullable().optional(),
  allDay: z.boolean().optional(),
  remindAt: isoDateTime.nullable().optional(),
  recurrence: recurrenceSchema.nullable().optional(),
  checklist: z.array(checklistItemSchema).max(50).optional(),
  source: z.enum(TASK_SOURCES).optional(),
});
export type TaskCreateInput = z.infer<typeof taskCreateSchema>;

export const taskUpdateSchema = taskCreateSchema
  .omit({ source: true })
  .extend({ status: z.enum(TASK_STATUSES) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Niente da salvare' });
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>;

export const TASK_VIEWS = ['oggi', 'prossime', 'aperte', 'fatte'] as const;
export type TaskView = (typeof TASK_VIEWS)[number];

export const taskListQuerySchema = z.object({
  view: z.enum(TASK_VIEWS).default('aperte'),
  pageId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export interface Task {
  id: string;
  pageId: string | null;
  title: string;
  notes: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueAt: string | null;
  allDay: boolean;
  remindAt: string | null;
  recurrence: Recurrence | null;
  checklist: ChecklistItem[];
  source: TaskSource;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface TaskCompleteResult {
  task: Task;
  /** La prossima occorrenza creata se l'attività si ripete. */
  next: Task | null;
}
